import { useEffect, useMemo, useRef, useState } from 'react';
import type { OrderHistory } from '../stores/appOrderHistoryStore';
import type { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { fetchExpectedInventory, ExpectedInventoryBootstrap } from '../services/inventory/expectedInventoryService';
import { expectedInventoryDate } from '../utils/reports/expectedInventory';
import { computeStockAdjustment, localDay, StockAdjustment } from '../utils/inventoryAdjustedCostPercent';

const MAX_PARALLEL = 3;

/** Fetched once per dashboard visit (no cross-visit cache: count corrections must show on return). */
async function loadAll(restaurantIds: string[]): Promise<ExpectedInventoryBootstrap[]> {
  const result: ExpectedInventoryBootstrap[] = [];
  for (let i = 0; i < restaurantIds.length; i += MAX_PARALLEL) {
    result.push(...await Promise.all(restaurantIds.slice(i, i + MAX_PARALLEL).map(id => fetchExpectedInventory(id))));
  }
  return result;
}

interface Params {
  enabled: boolean;
  accountId: string | undefined;
  restaurantIds: string[];
  startDate: Date;
  endDate: Date;
  /** The restaurant's full order and revenue history, used to move linked stock to the period edges. */
  orders: OrderHistory[];
  revenue: RevenueHistory[];
  historyReady: boolean;
  showExVatOrders: boolean;
}

/**
 * Dashboard stock-adjusted cost % stock terms (spec-inventory-group-types.md, phase 2).
 * `enabled` must already include the access + view gates; this adds the ended-period gate.
 * Returns null until every selected restaurant's inventory and the full history are loaded.
 */
export function useStockAdjustedCost(params: Params): StockAdjustment | null {
  const { enabled, accountId, restaurantIds, startDate, endDate, historyReady } = params;
  const startDay = localDay(startDate);
  const endDay = localDay(endDate);
  const periodEnded = endDay < expectedInventoryDate(Date.now());
  const active = enabled && periodEnded && !!accountId && restaurantIds.length === 1;
  const restaurantKey = restaurantIds.join(',');

  const [loaded, setLoaded] = useState<{ key: string; bootstraps: ExpectedInventoryBootstrap[] } | null>(null);
  const loadedKey = useRef<string | null>(null);
  useEffect(() => {
    const key = `${accountId}|${restaurantKey}`;
    if (!active || loadedKey.current === key) return;
    let cancelled = false;
    loadAll(restaurantKey.split(','))
      .then(bootstraps => { if (!cancelled) { loadedKey.current = key; setLoaded({ key, bootstraps }); } })
      .catch(error => { if (!cancelled) console.error('Stock-adjusted cost %: inventory load failed', error); });
    return () => { cancelled = true; };
  }, [active, accountId, restaurantKey]);

  const { orders, revenue, showExVatOrders } = params;
  return useMemo(() => {
    if (!active || !historyReady || !loaded || loaded.key !== `${accountId}|${restaurantKey}`) return null;
    return computeStockAdjustment({
      restaurantId: restaurantKey,
      bootstrap: loaded.bootstraps[0],
      orders, revenue, startDay, endDay, showExVatOrders, now: Date.now(),
    });
  }, [active, historyReady, loaded, accountId, restaurantKey, orders, revenue, startDay, endDay, showExVatOrders]);
}
